/**
 * TrainerSessions — full list of MY sessions (Vague 100).
 * Mobile-first: card list with status badges, tap → open in admin /app/sessions
 * (read-write per user choice 2a). Filters: status (all / upcoming / past).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { Loader2, Calendar, MapPin, Clock as ClockIcon } from 'lucide-react';

export default function TrainerSessions() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('upcoming');
  useEffect(() => {
    api.get('/me/trainer/sessions').then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    const now = new Date().toISOString().slice(0, 10);
    if (filter === 'upcoming') return items.filter((s) => (s.startDate || '') >= now);
    if (filter === 'past') return items.filter((s) => (s.startDate || '') < now);
    return items;
  }, [items, filter]);

  return (
    <div className="space-y-4" data-testid="trainer-sessions">
      <h1 className="text-[22px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mes sessions</h1>

      <div className="flex items-center gap-2">
        {[
          { id: 'upcoming', label: 'À venir' },
          { id: 'past', label: 'Passées' },
          { id: 'all', label: 'Toutes' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`trainer-sessions-filter-${f.id}`}
            className={`h-8 px-3 rounded-lg text-[12px] font-semibold transition-colors ${filter === f.id ? 'brand-gradient text-white' : 'bg-white border border-[#EFF0F6] text-[#4A4B63]'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered === null && (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" /></div>
      )}
      {filtered && filtered.length === 0 && (
        <div className="soft-card p-6 text-center text-[12.5px] italic text-[#8E8FA5]">Aucune session {filter === 'upcoming' ? 'à venir' : filter === 'past' ? 'passée' : 'enregistrée'}.</div>
      )}
      {filtered && filtered.map((s) => (
        <Link
          key={s.id}
          to={`/trainer/sessions/${s.id}`}
          className="soft-card p-4 block hover:border-[#C7D2FE] transition-colors"
          data-testid={`trainer-session-${s.id}`}
        >
          <div className="flex items-start justify-between mb-1.5">
            <div className="text-[14px] font-semibold text-[#1A1B2E] flex-1 min-w-0 truncate">{s.title || s.program || 'Session'}</div>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${statusStyle(s.status)}`}>{s.status || '—'}</span>
          </div>
          <div className="text-[12px] text-[#4A4B63] flex flex-wrap gap-x-4 gap-y-1">
            {s.startDate && (
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.startDate}{s.endDate && s.endDate !== s.startDate && ` → ${s.endDate}`}</span>
            )}
            {s.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.location}</span>}
            {s.duration && <span className="inline-flex items-center gap-1"><ClockIcon className="w-3 h-3" /> {s.duration}h</span>}
          </div>
          {Array.isArray(s.learners) && s.learners.length > 0 && (
            <div className="text-[11px] text-[#8E8FA5] mt-1.5">{s.learners.length} apprenant{s.learners.length > 1 ? 's' : ''}</div>
          )}
        </Link>
      ))}
    </div>
  );
}

function statusStyle(s) {
  const k = (s || '').toLowerCase();
  if (k.includes('terminée') || k === 'finie') return 'bg-[#E6FBF7] text-[#0F766E]';
  if (k.includes('cours')) return 'bg-[#EEF2FF] text-[#4F46E5]';
  if (k.includes('annul')) return 'bg-[#FEE2E2] text-[#B91C1C]';
  return 'bg-[#FEF3C7] text-[#92400E]';
}
